"use client";
import React from "react";

interface Picture {
    data: {
        id: string;
        attributes: {
            url: string;
            name: string;
            alternativeText: string;
        };
    };
}

interface Card {
    title: string;
    text: string;
    href: string;
    hrefText: string;
    image: Picture;
}

interface DataDisplayProps {
    data: {
        id: number;
        title: string;
        subtitle: string;
        Card: Card[];
        isLive: boolean;
        useH1Tag?: boolean;
    };
}

export default function DataDisplay_Invention_Layout1({ data }: DataDisplayProps) {
    const heading = data.title.split(':');
    return (
        data.isLive && (
            <section className="relative py-16 md:py-24 px-4 overflow-hidden bg-gradient-to-br from-amber-50 to-stone-100">
                {/* Background Pattern with Leaves */}
                <div className="absolute inset-0 z-0 opacity-20">
                    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
                        <defs>
                            <pattern id="leafPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                                <path d="M30,50 Q50,30 70,50 T90,70 Q70,90 50,70 T30,50 Z" fill="#a1887f" opacity="0.2" />
                                <path d="M10,30 Q30,10 50,30 T70,50 Q50,70 30,50 T10,30 Z" fill="#8d6e63" opacity="0.15" />
                                <path d="M60,20 Q80,40 70,60 T50,80 Q30,60 40,40 T60,20 Z" fill="#795548" opacity="0.1" />
                            </pattern>
                        </defs>
                        <rect x="0" y="0" width="100%" height="100%" fill="url(#leafPattern)" />
                    </svg>
                </div>

                {/* Decorative corner elements */}
                <div className="absolute top-0 left-0 w-40 h-40 opacity-10">
                    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0,0 C30,10 10,30 0,50 C10,70 30,90 50,100 C70,90 90,70 100,50 C90,30 70,10 50,0 C30,10 10,30 0,50 Z" fill="#5d4037" />
                    </svg>
                </div>

                <div className="absolute bottom-0 right-0 w-40 h-40 opacity-10 rotate-180">
                    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0,0 C30,10 10,30 0,50 C10,70 30,90 50,100 C70,90 90,70 100,50 C90,30 70,10 50,0 C30,10 10,30 0,50 Z" fill="#5d4037" />
                    </svg>
                </div>

                <div className="max-w-5xl mx-auto relative z-10">
                    {/* Header section */}
                    <div className="text-center mb-16">
                        {data.subtitle && (
                            <span className="text-sm font-semibold text-amber-800 uppercase tracking-wider mb-2 block">
                                {data.subtitle}
                            </span>
                        )}

                        {data?.useH1Tag ? (
                            <h1 className="text-4xl md:text-5xl   text-stone-900 mb-6">
                                {heading[0]}
                            </h1>
                        ) : (
                            <h2 className="text-4xl md:text-5xl   text-stone-900 mb-6">
                                {heading[0]}
                            </h2>
                        )}

                        {
                            heading[1] && (
                                <h2 className="text-xl md:text-2xl   text-slate-800 mb-6">
                                    {heading[1]}
                                </h2>
                            )
                        }

                        {/* Decorative divider */}
                        <div className="flex justify-center">
                            <div className="w-24 h-1 bg-amber-700 rounded-full"></div>
                        </div>
                    </div>

                    {/* Cards grid */}
                    <div className="grid md:grid-cols-2 gap-8 md:gap-10">
                        {data.Card.map((item, idx) => (
                            <div key={idx} className="group bg-white rounded-xl p-8 shadow-md border border-stone-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-2">
                                <div className="flex flex-col h-full">
                                    {/* Card header with number and icon */}
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-amber-200 transition-colors duration-300">
                                            <span className="text-stone-900 font-bold text-xl">{idx + 1}</span>
                                        </div>
                                        <h3 className="text-2xl font-semibold text-stone-900">
                                            {item?.title}
                                        </h3>
                                    </div>

                                    {/* Card content */}
                                    <div className="flex-grow">
                                        {/* Card description */}
                                        {item.hrefText && (
                                            <p className="text-stone-700 mb-4 leading-relaxed">
                                                {item?.hrefText}
                                            </p>
                                        )}

                                        {/* Additional text */}
                                        {item.text && (
                                            <p className="text-stone-800 font-medium mb-6">
                                                {item?.text}
                                            </p>
                                        )}
                                    </div>

                                    {/* Learn more link */}
                                    {item.href && (
                                        <div className="mt-auto pt-4 border-t border-stone-100">
                                            <a href={item.href} className="inline-flex items-center text-amber-700 hover:text-amber-900 font-medium transition-colors duration-200">
                                                <span className="mr-2">Discover more</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {/* Decorative corner element */}
                                <div className="absolute bottom-4 right-4 opacity-20">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 2C15 2 15 6 15 9C15 12 18 12 21 12C21 15 18 15 15 15C15 18 15 21 12 21C9 21 9 18 9 15C6 15 3 15 3 12C3 9 6 9 9 9C9 6 9 2 12 2Z" stroke="#5d4037" strokeWidth="1.5" />
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        )
    );
}